import React, { useState } from 'react'
import { Skeleton } from '@chakra-ui/core'
import { Globe } from 'lucide-react'
import { APIResponse } from 'components/LinkPreview'
import useLocaleText from 'components/useLocaleText'
import { getLinkPopUrl } from './model/linkPop'
import { linkPopLiterals } from './model/literals'
import styles from './LinkPopPreview.module.css'

interface ILinkPopPreviewProps {
  url: string | null
  metadata: APIResponse | null
  loading: boolean
  variant?: 'card' | 'compact' | 'action'
  renderAction?: (image: React.ReactNode, title: string) => React.ReactNode
}

export function LinkPopPreview({ url, metadata, loading, variant = 'card', renderAction }: ILinkPopPreviewProps) {
  const preview = useLocaleText(linkPopLiterals.preview)

  const [failedImage, setFailedImage] = useState<string | null>(null)

  const isCompact = variant === 'compact'
  const isAction = variant === 'action'
  const safeUrl = getLinkPopUrl(url || undefined)
  const hostname = safeUrl ? new URL(safeUrl).hostname : ''
  const image = typeof metadata?.image === 'string' ? metadata.image : null
  const metadataTitle = typeof metadata?.title === 'string' ? metadata.title.trim() : ''
  const siteName = typeof metadata?.siteName === 'string' ? metadata.siteName.trim() : ''
  const title = metadataTitle || siteName || hostname || preview
  const site = siteName || hostname
  const showSite = title.toLowerCase() !== site.toLowerCase()

  const previewImage = (
    <span className={styles.previewImage} aria-hidden="true">
      {loading ? (
        <Skeleton width="100%" height="100%" />
      ) : image && image !== failedImage ? (
        <img src={image} alt="" onError={() => setFailedImage(image)} />
      ) : (
        <Globe size={isCompact ? 17 : isAction ? 20 : 26} strokeWidth={1.5} />
      )}
    </span>
  )

  if (isAction) {
    return (
      <div className={styles.action} role="region" aria-label={preview}>
        {renderAction?.(previewImage, loading ? '' : metadataTitle)}
      </div>
    )
  }

  return (
    <div
      className={`${styles.preview} ${isCompact ? styles.compact : ''}`}
      role="region"
      aria-label={preview}
      aria-busy={loading}
    >
      {previewImage}
      <div className={styles.previewContent}>
        {loading ? (
          <div className={styles.previewSkeleton} aria-hidden="true">
            <Skeleton height={isCompact ? '11px' : '9px'} width={isCompact ? '104px' : '40%'} />
            <Skeleton height={isCompact ? '9px' : '12px'} width={isCompact ? '56px' : '90%'} />
            {!isCompact && <Skeleton height="9px" width="65%" />}
          </div>
        ) : isCompact ? (
          <>
            <div className={styles.previewTitle} dir="auto" title={title}>
              {title}
            </div>
            {showSite && (
              <>
                <span className={styles.separator} aria-hidden="true">
                  ·
                </span>
                <div className={styles.siteName} dir="auto" title={site}>
                  {site}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className={styles.hostname} dir="ltr">
              {hostname}
            </div>
            <div className={styles.previewTitle} dir="auto">
              {title}
            </div>
            <div className={styles.url} dir="ltr">
              {url}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
